import React, { useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import noop from 'lodash/noop';
import { asyncCreateIssueTypeModalEntryPoint } from '@atlassian/jira-create-issue-type-modal/entrypoint.tsx';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { RuntimePropsOfEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import type { ConfigureSource } from '@atlassian/jira-issue-status-and-types-common/src/types.tsx';

export type CreateIssueTypeModalProps = {
	onIssueTypeCreated?: () => void;
	onOpen: () => void;
	onClose: () => void;
	projectKey: string;
	shouldFetchProjectData?: boolean;
	source: ConfigureSource;
};
type RuntimeProps = RuntimePropsOfEntryPoint<typeof asyncCreateIssueTypeModalEntryPoint>;

export const CreateIssueTypeModal = forwardRef(
	(
		{
			onIssueTypeCreated = noop,
			onOpen,
			onClose,
			source,
			shouldFetchProjectData = false,
			projectKey,
		}: CreateIssueTypeModalProps,
		ref,
	) => {
		const cloudId = useCloudId();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
			asyncCreateIssueTypeModalEntryPoint,
			{
				projectKey,
				cloudId,
				shouldFetchProjectData,
			},
		);

		useImperativeHandle(ref, () => ({
			load: entryPointActions.load,
			preload: entryPointActions.preload,
		}));

		const onCloseCreateIssueTypeModal = useCallback(() => {
			fireUIAnalytics(
				createAnalyticsEvent({
					actionSubject: 'inlineCreateIssueTypeModal',
					action: 'closed',
				}),
			);
			onClose();
		}, [createAnalyticsEvent, onClose]);

		const entryPointProps: RuntimeProps = useMemo(
			() => ({
				onIssueTypeCreated,
				issueTypesCountLimit: 30,
				onClose: onCloseCreateIssueTypeModal,
				onOpen,
				source,
			}),
			[onCloseCreateIssueTypeModal, onIssueTypeCreated, onOpen, source],
		);

		return (
			<ModalEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				entryPointActions={entryPointActions}
				runtimeProps={entryPointProps}
				id="create-issue-type-modal"
			/>
		);
	},
);
